import { useState, useEffect } from 'react';
import { ImHeart, ImSpades } from "react-icons/im";
import { GiClubs } from "react-icons/gi";
import styles from './A23GameCard.module.css';
import { AnimatePresence, motion } from 'motion/react';
import { RiPokerDiamondsFill } from "react-icons/ri";
import useWinningHand from '@/src/hooks/data/useWinningHand';
import { CLIENT_CARDS, GAME_STAGE_SHOWDOWN, L_CARD, M_CARD, OPPONENT_CARDS, TEXAS_HOLDEM, S_CARD, XS_CARD, XL_CARD } from '@/Poker/data/Constants';
import { IoEye } from "react-icons/io5";
import { useShowCommunityCardsImmediately } from '@/src/hooks/data/useGameTable';
import { COMMUNITY_CARDS, GAME_STAGE_FLOP } from '@/Poker/data/Constants';
import useGameTableDimensions from '@/src/hooks/data/useGameTableDimensions';
import gameAssets from '@themed-assets';

export default function A23GameCard({ value, style, size = M_CARD, tempTableId, type, gameStage, index, onClick, shownCard, showAllCards, gameType, layout }) {
  const [suit, setSuit] = useState(null);
  const [rank, setRank] = useState(null);
  const [color, setColor] = useState(null);
  const winningHand = useWinningHand(tempTableId)
  const showCommunityCardsImmediately = useShowCommunityCardsImmediately(tempTableId);
  const gameTableDimensions = useGameTableDimensions(tempTableId);
  const cardWidth = type === COMMUNITY_CARDS ? gameTableDimensions.width * 0.6 / 5 : null;
  const flopCards = type === COMMUNITY_CARDS && gameStage === GAME_STAGE_FLOP && index <= 2;

  useEffect(() => {
    if (!value || value.length === 0) return;
    setSuit(value[0]);
    setRank(value?.slice(1));
    setColor(value[0] === 'h' || value[0] === 'd' ? '#B30101' : 'black');
  }, [value]);

  const suitSymbols = {
    h: <ImHeart size={cardWidth ? cardWidth * 0.5 : size === XL_CARD ? 32 : size === L_CARD ? 24 : size === M_CARD ? 20 : size === S_CARD ? 14 : size === XS_CARD ? 10 : 8} color="#B30101" />, // Hearts
    d: <RiPokerDiamondsFill size={cardWidth ? cardWidth * 0.5 : size === XL_CARD ? 32 : size === L_CARD ? 24 : size === M_CARD ? 20 : size === S_CARD ? 14 : size === XS_CARD ? 10 : 8} color="#B30101" />, // Diamonds
    c: <GiClubs size={cardWidth ? cardWidth * 0.5 : size === XL_CARD ? 32 : size === L_CARD ? 24 : size === M_CARD ? 20 : size === S_CARD ? 14 : size === XS_CARD ? 10 : 8} color="black" />, // Clubs
    s: <ImSpades size={cardWidth ? cardWidth * 0.5 : size === XL_CARD ? 32 : size === L_CARD ? 24 : size === M_CARD ? 20 : size === S_CARD ? 14 : size === XS_CARD ? 10 : 8} color="black" />  // Spades
  };

  const smallSuitSymbols = {
    h: <ImHeart size={cardWidth ? cardWidth * 0.3 : size === XL_CARD ? 18 : size === L_CARD ? 12 : size === M_CARD ? 12 : size === S_CARD ? 8 : size === XS_CARD ? 6 : 4} color="#B30101" />, // Hearts
    d: <RiPokerDiamondsFill size={cardWidth ? cardWidth * 0.3 : size === XL_CARD ? 18 : size === L_CARD ? 12 : size === M_CARD ? 12 : size === S_CARD ? 8 : size === XS_CARD ? 6 : 4} color="#B30101" />, // Diamonds
    c: <GiClubs size={cardWidth ? cardWidth * 0.3 : size === XL_CARD ? 18 : size === L_CARD ? 12 : size === M_CARD ? 12 : size === S_CARD ? 8 : size === XS_CARD ? 6 : 4} color="black" />, // Clubs
    s: <ImSpades size={cardWidth ? cardWidth * 0.3 : size === XL_CARD ? 18 : size === L_CARD ? 12 : size === M_CARD ? 12 : size === S_CARD ? 8 : size === XS_CARD ? 6 : 4} color="black" />  // Spades
  };

  const rankDisplay = {
    '1': 'A',  // Ace
    '2': '2',
    '3': '3',
    '4': '4',
    '5': '5',
    '6': '6',
    '7': '7',
    '8': '8',
    '9': '9',
    '10': '10',
    '11': 'J', // Jack
    '12': 'Q', // Queen
    '13': 'K'  // King
  };

  // Initial configs per type
  const getInitialForCommunity = () => ({
    scale: 0,
    opacity: 0,
    x: flopCards ? -50 * index : 0,
  });

  const getInitialForClient = () => ({
    scale: 0.85,
    opacity: 0,
    x: 0,
    y: gameTableDimensions && gameTableDimensions.height ? -gameTableDimensions.height * 0.25 : -200,
    // rotate: index % 2 === 0 ? -12 : 12,
  });

  const getInitialForOpponent = () => ({
    scale: 1,
    opacity: 1,
    x: 0,
  });

  // Animate configs per type
  const getAnimateForCommunity = () => ({
    scale: 1,
    x: 0,
    opacity: value === 'cardBack' ? 0 : 1,
    filter: winningHand.includes(value)
      ? 'drop-shadow(0 0 8px var(--color-focus))'
      : (winningHand.length > 0 && !shownCard ? 'brightness(0.5)' : null),
    rotate: 0,
    border: winningHand.includes(value) ? '1px solid var(--color-focus)' : null,
    // use drop-shadow instead of boxShadow to avoid clipping with images
  });

  const getAnimateForClient = () => ({
    scale: 1,
    x: 0,
    y: 0,
    opacity: value === 'cardBack' ? 0 : 1,
    // filter applied on inner card during flip branch
    rotate: 0,
  });

  const getAnimateForOpponent = () => ({
    scale: 1,
    x: 0,
    opacity: value === 'cardBack' ? 0 : 1,
    // filter applied on inner card during flip branch
    rotate: 0,
  });

  // Transition configs per type
  const getTransitionForCommunity = () => {
    let delay = 0;
    if (!showCommunityCardsImmediately) {
      if (index >= 3) {
        if (gameStage === GAME_STAGE_SHOWDOWN) {
          if (index === 3) {
            delay = 1.5;
          } else if (index === 4) {
            delay = 3;
          } else {
            delay = 1;
          }
        } else {
          delay = 0;
        }
      }
    }
    return {
      layout: {
        duration: 0.3,
        ease: "easeInOut"
      },
      opacity: { delay },
      scale: { delay },
      x: { duration: 0.5 },
    };
  };

  const getTransitionForClient = () => {
    const delay = gameType === TEXAS_HOLDEM ? index * 0.5 : index * 0.25;
    return {
      layout: {
        duration: 0.3,
        ease: "easeInOut"
      },
      opacity: { delay },
      scale: { delay },
      x: { type: 'spring', stiffness: 500, damping: 30, delay },
      y: { type: 'spring', stiffness: 500, damping: 30, delay },
      rotate: { type: 'spring', stiffness: 400, damping: 35, delay },
    };
  };

  const getTransitionForOpponent = () => {
    const delay = gameType === TEXAS_HOLDEM ? index * 0.5 : index * 0.25;
    return {
      layout: {
        duration: 0.3,
        ease: "easeInOut"
      },
      opacity: { delay },
      scale: { delay },
      x: { type: 'spring', stiffness: 500, damping: 30, delay },
      y: { type: 'spring', stiffness: 500, damping: 30, delay },
      rotate: { type: 'spring', stiffness: 400, damping: 35, delay },
    };
  };

  const getConfigsByType = () => {
    if (type === COMMUNITY_CARDS) {
      return {
        initial: getInitialForCommunity(),
        animate: getAnimateForCommunity(),
        transition: getTransitionForCommunity(),
      };
    }
    if (type === CLIENT_CARDS) {
      return {
        initial: getInitialForClient(),
        animate: getAnimateForClient(),
        transition: getTransitionForClient(),
      };
    }
    if (type === OPPONENT_CARDS) {
      return {
        initial: getInitialForOpponent(),
        animate: getAnimateForOpponent(),
        transition: getTransitionForOpponent(),
      };
    }
    // Fallback
    return {
      initial: getInitialForClient(),
      animate: getAnimateForClient(),
      transition: getTransitionForClient(),
    };
  };

  const configs = getConfigsByType();

  // Special flip render for opponent cards
  if (type === OPPONENT_CARDS) {
    const delay = gameType === TEXAS_HOLDEM ? index * 0.5 : index * 0.25;
    // Only motion props needed for position/opacity on wrapper
    const wrapperAnimate = { scale: 1, x: 0, opacity: value === 'cardBack' ? 0 : 1 };
    const highlightStyle = winningHand.includes(value)
      ? { border: '1px solid var(--color-focus)', filter: 'drop-shadow(0 0 8px var(--color-focus))' }
      : { filter: winningHand.length > 0 && !shownCard ? 'brightness(0.5)' : null };

    return (
      <AnimatePresence>
        <motion.div
          layout={layout}
          onClick={onClick}
          initial={configs.initial}
          animate={wrapperAnimate}
          transition={configs.transition}
          exit={{ scale: 0 }}
          style={{ width: cardWidth ? cardWidth : null, maxWidth: cardWidth ? cardWidth : null, height: cardWidth ? cardWidth * 1.35 : null, perspective: 1000, padding: 0, ...style }}
        >
          <motion.div
            className={`${styles.card} ${styles[size]}`}
            initial={{ rotateY: 180 }}
            animate={{ rotateY: 0 }}
            transition={{ delay, duration: 0.6, ease: "easeInOut" }}
            style={{ transformStyle: 'preserve-3d', ...highlightStyle }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div className={styles.cardTop}>
                <span className={styles.cardRank} style={{ color }}>{rankDisplay[rank]}</span>
                <div className={styles.smallSuit}>
                  {smallSuitSymbols[suit]}
                </div>
              </div>
              <div className={styles.cardSuit}>
                {suitSymbols[suit]}
              </div>
            </div>

            <div
              style={{
                position: 'absolute',
                inset: 0,
                transform: 'rotateY(180deg)',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img src={gameAssets.cardback} alt="card back" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', borderRadius: 'inherit' }} />
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Special flip render for client cards
  if (type === CLIENT_CARDS) {
    const delay = gameType === TEXAS_HOLDEM ? index * 0.5 : index * 0.25;
    // Only motion props needed for position/opacity on wrapper
    const wrapperAnimate = { scale: 1, x: 0, y: 0, opacity: value === 'cardBack' ? 0 : 1 };
    const highlightStyle = winningHand.includes(value)
      ? { border: '1px solid var(--color-focus)', filter: 'drop-shadow(0 0 8px var(--color-focus))' }
      : { filter: winningHand.length > 0 && !shownCard ? 'brightness(0.5)' : null };

    return (
      <AnimatePresence>
        <motion.div
          layout={layout}
          onClick={onClick}
          initial={configs.initial}
          animate={wrapperAnimate}
          transition={configs.transition}
          exit={{ scale: 0 }}
          style={{ width: cardWidth ? cardWidth : null, maxWidth: cardWidth ? cardWidth : null, height: cardWidth ? cardWidth * 1.35 : null, perspective: 1000, padding: 0, ...style }}
        >
          <motion.div
            className={`${styles.card} ${styles[size]}`}
            initial={{ rotateY: 180, opacity: 1 }}
            animate={{ rotateY: 0, opacity: 1 }}
            transition={{ delay, duration: 0.6, ease: "easeInOut" }}
            style={{ transformStyle: 'preserve-3d', ...highlightStyle }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div className={styles.cardTop}>
                <span className={styles.cardRank} style={{ color }}>{rankDisplay[rank]}</span>
                <div className={styles.smallSuit}>
                  {smallSuitSymbols[suit]}
                </div>
              </div>
              <div className={styles.cardSuit}>
                {suitSymbols[suit]}
              </div>
              {shownCard && !showAllCards ? (
                <div className={styles.eyeBall}>
                  <IoEye size={8} color="var(--color-focus)" />
                </div>
              ) : null}
            </div>

            <div
              style={{
                position: 'absolute',
                inset: 0,
                transform: 'rotateY(180deg)',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img src={gameAssets.cardback} alt="card back" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', borderRadius: 'inherit' }} />
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        layout={layout}
        onClick={onClick}
        initial={configs.initial}
        animate={configs.animate}
        transition={configs.transition}
        exit={{
          scale: 0,
        }}
        style={{ width: cardWidth ? cardWidth : null, maxWidth: cardWidth ? cardWidth : null, height: cardWidth ? cardWidth * 1.35 : null, ...style }} className={`${styles.card} ${styles[size]}`}>
        <div className={styles.cardTop}>
          <span className={styles.cardRank} style={{ color }}>{rankDisplay[rank]}</span>
          <div className={styles.smallSuit}>
            {smallSuitSymbols[suit]}
          </div>
        </div>
        <div className={styles.cardSuit}>
          {suitSymbols[suit]}
        </div>
        {
          shownCard && !showAllCards ?
            <div className={styles.eyeBall}>
              <IoEye size={8} color="var(--color-focus)" />
            </div>
            :
            null
        }
      </motion.div>
    </AnimatePresence>
  );
} 